import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { isApp, isIosApp, isAndroidApp } from './utils.js';

// Web Firebase Analytics (only loaded for web)
let webAnalytics = null;
if (!isApp) {
  // Dynamically import Firebase web SDK only for web platform
  import('firebase/app').then(async (firebase) => {
    const { initializeApp } = firebase;
    const { getAnalytics, logEvent, setUserProperties, setUserId } = await import('firebase/analytics');
    const { firebaseConfigWeb } = await import('../firebase.config');
    
    try {
      const app = initializeApp(firebaseConfigWeb);
      webAnalytics = getAnalytics(app);
      console.log('Web Firebase Analytics initialized');
    } catch (error) {
      console.error('Web Firebase Analytics initialization failed:', error);
    }
  }).catch(error => {
    console.log('Firebase web SDK not available:', error);
  });
}

/**
 * Analytics Service for Google Analytics & Google Ads Events
 * Tracks user interactions, conversions, and app engagement
 */
class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.eventQueue = [];  // Queue for events fired before initialization
  }

  /**
   * Initialize analytics (called from main.ts)
   */
  async initialize() {
    if (!this.isInitialized) {
      try {
        // iOS needs extra time for Firebase to fully initialize
        if (isIosApp) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Test that Firebase Analytics is actually working
          try {
            await FirebaseAnalytics.logEvent({
              name: 'analytics_initialized',
              params: { platform: 'ios' }
            });
          } catch (error) {
            // Silent fail - analytics might still work
          }
        }
        
        this.isInitialized = true;
        
        // Process any queued events
        if (this.eventQueue.length > 0) {
          for (const event of this.eventQueue) {
            await this.logEvent(event.name, event.parameters);
          }
          this.eventQueue = [];
        }
      } catch (error) {
        // Silent fail
      }
    }
  }

  /**
   * Log custom event with parameters
   * @param {string} eventName - Event name
   * @param {object} parameters - Event parameters
   */
  async logEvent(eventName, parameters = {}) {
    if (!this.isInitialized) {
      // Queue the event to be sent once initialized
      this.eventQueue.push({ name: eventName, parameters });
      return;
    }
    
    const eventData = {
      timestamp: new Date().toISOString(),
      ...parameters
    };
    
    try {
      if (isApp) {
        // Mobile app - use Capacitor Firebase Analytics
        await FirebaseAnalytics.logEvent({
          name: eventName,
          params: eventData
        });
      } else if (webAnalytics) {
        // Web - use Firebase web SDK
        const { logEvent } = await import('firebase/analytics');
        logEvent(webAnalytics, eventName, eventData);
      }
    } catch (error) {
      // Silent fail - don't interrupt user experience
    }
  }

  // ===========================================
  // USER ENGAGEMENT EVENTS
  // ===========================================

  /**
   * Track user login
   * @param {string} method - login method (google, email, apple, etc.)
   */
  async trackLogin(method = 'unknown') {
    await this.logEvent('login', {
      method: method
    });
  }

  /**
   * Track user registration/sign up
   * @param {string} method - signup method
   */
  async trackSignUp(method = 'email') {
    await this.logEvent('sign_up', {
      method: method
    });
  }

  /**
   * Track tutorial/onboarding completion
   */
  async trackTutorialComplete() {
    await this.logEvent('tutorial_complete', {});
  }

  /**
   * Track user profile completion
   */
  async trackProfileSetup() {
    await this.logEvent('profile_setup_complete', {});
  }

  // ===========================================
  // LANGUAGE LEARNING EVENTS
  // ===========================================

  /**
   * Track language selection
   * @param {string} language - Selected language code
   * @param {string} level - User's level (beginner, intermediate, advanced)
   */
  async trackLanguageSelected(language, level = 'unknown') {
    await this.logEvent('language_selected', {
      language: language,
      level: level
    });
  }

  /**
   * Track lesson start
   * @param {string} lessonType - Type of lesson (chat, flashcard, game, etc.)
   * @param {string} lessonId - Lesson identifier
   * @param {string} language - Target language
   */
  async trackLessonStart(lessonType, lessonId, language) {
    await this.logEvent('lesson_start', {
      lesson_type: lessonType,
      lesson_id: lessonId,
      language: language
    });
  }

  /**
   * Track lesson completion
   * @param {string} lessonType - Type of lesson
   * @param {string} lessonId - Lesson identifier
   * @param {string} language - Target language
   * @param {number} duration - Time spent in seconds
   * @param {number} score - User's score (0-100)
   */
  async trackLessonComplete(lessonType, lessonId, language, duration = 0, score = 0) {
    await this.logEvent('lesson_complete', {
      lesson_type: lessonType,
      lesson_id: lessonId,
      language: language,
      duration_seconds: duration,
      score: score
    });
  }

  /**
   * Track streak milestone
   * @param {number} streakDays - Number of consecutive days
   */
  async trackStreakMilestone(streakDays) {
    await this.logEvent('streak_milestone', {
      streak_days: streakDays
    });
  }

  // ===========================================
  // CHAT MODE EVENTS
  // ===========================================

  /**
   * Track chat session start
   * @param {string} chatType - Type of chat (free, roleplay, debate, etc.)
   * @param {string} language - Target language
   */
  async trackChatStart(chatType, language) {
    await this.logEvent('chat_start', {
      chat_type: chatType,
      language: language
    });
  }

  /**
   * Track message sent in chat
   * @param {string} language - Target language
   * @param {number} messageLength - Character count
   */
  async trackMessageSent(language, messageLength = 0) {
    await this.logEvent('message_sent', {
      language: language,
      message_length: messageLength
    });
  }

  /**
   * Track AI correction received
   * @param {string} language - Target language
   * @param {string} correctionType - Type of correction (grammar, vocabulary, etc.)
   */
  async trackCorrectionReceived(language, correctionType) {
    await this.logEvent('correction_received', {
      language: language,
      correction_type: correctionType
    });
  }

  // ===========================================
  // FLASHCARD EVENTS
  // ===========================================

  /**
   * Track flashcard session start
   * @param {string} deckId - Deck identifier
   * @param {string} language - Target language
   */
  async trackFlashcardStart(deckId, language) {
    await this.logEvent('flashcard_start', {
      deck_id: deckId,
      language: language
    });
  }

  /**
   * Track flashcard answer
   * @param {string} cardId - Card identifier
   * @param {boolean} correct - Whether answer was correct
   * @param {number} responseTime - Time to answer in seconds
   */
  async trackFlashcardAnswer(cardId, correct, responseTime = 0) {
    await this.logEvent('flashcard_answer', {
      card_id: cardId,
      correct: correct,
      response_time_seconds: responseTime
    });
  }

  // ===========================================
  // SUBSCRIPTION & MONETIZATION EVENTS
  // ===========================================

  /**
   * Track subscription view
   * @param {string} source - Where user came from (paywall, settings, etc.)
   */
  async trackSubscriptionView(source = 'unknown') {
    await this.logEvent('subscription_view', {
      source: source
    });
  }

  /**
   * Track subscription purchase attempt
   * @param {string} planId - Subscription plan identifier
   * @param {number} price - Price in local currency
   * @param {string} currency - Currency code
   */
  async trackSubscriptionAttempt(planId, price, currency) {
    await this.logEvent('begin_checkout', {
      currency: currency,
      value: price,
      items: [{
        item_id: planId,
        item_name: `Lingo Premium ${planId}`,
        item_category: 'subscription',
        price: price
      }]
    });
  }

  /**
   * Track successful subscription purchase
   * @param {string} planId - Subscription plan identifier
   * @param {number} price - Price in local currency
   * @param {string} currency - Currency code
   * @param {string} transactionId - Transaction identifier
   */
  async trackSubscriptionPurchase(planId, price, currency, transactionId) {
    // Standard ecommerce purchase event
    await this.logEvent('purchase', {
      transaction_id: transactionId,
      currency: currency,
      value: price,
      items: [{
        item_id: planId,
        item_name: `Lingo Premium ${planId}`,
        item_category: 'subscription',
        price: price,
        quantity: 1
      }]
    });

    // Custom subscription event
    await this.logEvent('subscription_purchase', {
      plan_id: planId,
      price: price,
      currency: currency,
      transaction_id: transactionId
    });
  }

  /**
   * Track ad view
   * @param {string} adType - Type of ad (banner, interstitial, rewarded)
   * @param {string} placement - Where ad was shown
   */
  async trackAdView(adType, placement) {
    await this.logEvent('ad_impression', {
      ad_platform: 'admob',
      ad_source: 'admob',
      ad_format: adType,
      ad_unit_name: placement
    });
  }

  /**
   * Track rewarded ad completion
   * @param {string} reward - What user earned
   */
  async trackRewardedAdComplete(reward) {
    await this.logEvent('ad_reward', {
      ad_platform: 'admob',
      reward_type: reward
    });
  }

  // ===========================================
  // CONTENT & FEATURE USAGE
  // ===========================================

  /**
   * Track article/content view
   * @param {string} articleId - Article identifier
   * @param {string} category - Content category
   * @param {string} language - Content language
   */
  async trackContentView(articleId, category, language) {
    await this.logEvent('select_content', {
      content_type: 'article',
      item_id: articleId,
      content_category: category,
      language: language
    });
  }

  /**
   * Track feature usage
   * @param {string} featureName - Name of feature used
   * @param {object} additionalParams - Additional parameters
   */
  async trackFeatureUsage(featureName, additionalParams = {}) {
    await this.logEvent('feature_usage', {
      feature_name: featureName,
      ...additionalParams
    });
  }

  /**
   * Track search performed
   * @param {string} searchTerm - What user searched for
   * @param {string} category - Search category
   */
  async trackSearch(searchTerm, category = 'general') {
    await this.logEvent('search', {
      search_term: searchTerm,
      search_category: category
    });
  }

  // ===========================================
  // USER PROPERTIES (for audience segmentation)
  // ===========================================

  /**
   * Set user properties for audience targeting
   * @param {object} properties - User properties
   */
  async setUserProperties(properties) {
    if (!this.isInitialized) return;

    try {
      if (isApp) {
        // Mobile app - use Capacitor Firebase Analytics
        for (const [key, value] of Object.entries(properties)) {
          await FirebaseAnalytics.setUserProperty({
            key: key,
            value: String(value)
          });
        }
      } else if (webAnalytics) {
        // Web - use Firebase web SDK
        const { setUserProperties } = await import('firebase/analytics');
        setUserProperties(webAnalytics, properties);
      }
      console.log(`User properties set (${isApp ? 'mobile' : 'web'}):`, properties);
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  /**
   * Set user ID for cross-platform tracking
   * @param {string} userId - Unique user identifier
   */
  async setUserId(userId) {
    if (!this.isInitialized) return;

    try {
      if (isApp) {
        // Mobile app - use Capacitor Firebase Analytics
        await FirebaseAnalytics.setUserId({
          userId: userId
        });
      } else if (webAnalytics) {
        // Web - use Firebase web SDK
        const { setUserId } = await import('firebase/analytics');
        setUserId(webAnalytics, userId);
      }
      console.log(`User ID set (${isApp ? 'mobile' : 'web'}):`, userId);
    } catch (error) {
      console.error('Failed to set user ID:', error);
    }
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();
export default analytics; 